import { createContext, useContext, useState, ReactNode } from "react";
import { useProfileContext } from "./ProfileContext";
import { Role } from "../core/models/entities/Role";

interface PriceVisibilityContextProps {
  hidePrices: boolean;
  showCustomerPrices: boolean;
  togglePriceVisibility: () => void;
  toggleCustomerPrices: () => void;
}

const PriceVisibilityContext = createContext<
  PriceVisibilityContextProps | undefined
>(undefined);

interface PriceVisibilityProviderProps {
  children: ReactNode;
}

export function PriceVisibilityProvider({
  children,
}: PriceVisibilityProviderProps) {
  const [hidePrices, setHidePrices] = useState<boolean>(false);
  const [showCustomerPrices, setShowCustomerPrices] = useState<boolean>(false);
  const { role, organization, workshopMode } = useProfileContext();

  const togglePriceVisibility = () => {
    setHidePrices((prev) => !prev);
  };

  const toggleCustomerPrices = () => {
    if (role === Role.EndUser || workshopMode) return;
    setShowCustomerPrices((prev) => !prev);
  };

  const customerHasPricesHidden =
    role === Role.EndUser && organization?.hidePrices === true;

  // OR semantics: the per-user workshop flag always wins - the session
  // "Hide Prices" toggle can never undo it.
  const effectiveHidePrices =
    hidePrices || customerHasPricesHidden || workshopMode;

  // Flagged users are treated like end users: if a price ever leaks through
  // a missing hide-gate, it is the customer-facing price, never vendor cost.
  const effectiveShowCustomerPrices =
    role === Role.EndUser || workshopMode ? true : showCustomerPrices;

  return (
    <PriceVisibilityContext.Provider
      value={{
        hidePrices: effectiveHidePrices,
        showCustomerPrices: effectiveShowCustomerPrices,
        togglePriceVisibility,
        toggleCustomerPrices,
      }}
    >
      {children}
    </PriceVisibilityContext.Provider>
  );
}

export function usePriceVisibility() {
  const context = useContext(PriceVisibilityContext);
  if (context === undefined) {
    throw new Error(
      "usePriceVisibility must be used within a PriceVisibilityProvider"
    );
  }
  return context;
}
